import React, { useEffect, useState, useMemo, useCallback } from 'react';
import moment from 'moment-timezone';
import { Box, Heading, CalendarViewTypes, DatePicker, TextField } from '../../../ui';
import { Grid } from '../../../ui/components/Grid';
import { useApi } from '../../../hooks';
import { AppLoadingOverlay, Dialog } from '../../../components';

const toMonthString = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const monthLabel = (monthStr) => {
  // Avoid `new Date('YYYY-MM-DD')` (UTC parsing can shift to prior month in some timezones)
  return moment(monthStr, 'YYYY-MM-DD').format('MMM YYYY');
};

export const MonthlyReportingView = () => {
  const { getMonthlyReporting } = useApi();
  const [data, setData] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [editingPicker, setEditingPicker] = useState(null); // 'start' | 'end' | null

  const [startMonth, setStartMonth] = useState(() => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - 3, 1);
    return toMonthString(start);
  });

  const [endMonth, setEndMonth] = useState(() => {
    const now = new Date();
    return toMonthString(now);
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMonthlyReporting({
        startMonth,
        endMonth,
      });

      const responseMonths = res && Array.isArray(res.months) ? res.months : [];
      const responsePolicies = res && Array.isArray(res.policies) ? res.policies : [];
      setMonths(responseMonths);
      setData(responsePolicies);
    } catch (err) {
      console.error(err);
      setMonths([]);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [getMonthlyReporting, startMonth, endMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getReportCellStyle = useCallback((value, monthStr) => {
    const pill = (bg, border, color) => ({
      backgroundColor: bg,
      border: `1px solid ${border}`,
      color,
      fontWeight: 600,
      textAlign: 'center',
      borderRadius: '8px',
      padding: '3px 8px',
    });

    // value meanings:
    // - null: not active in that month
    // - '': active but not reported
    // - 'YYYY-MM-DD': reported on date
    if (value) {
      return pill('#DCFCE7', '#22C55E', '#166534');
    }

    if (value === '') {
      return pill('#FEE2E2', '#EF4444', '#991B1B');
    }

    // Not active in that month
    return pill('#F3F4F6', '#E5E7EB', '#374151');
  }, []);

  const columns = useMemo(() => {
    const base = [
      { headerName: 'Insured', field: 'insured', flex: 1.5, sortable: true },
      { headerName: 'Policy Number', field: 'policyNumber', sortable: true },
      {
        headerName: 'Effective At',
        field: 'effectiveAt',
        sortable: true,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''),
      },
      {
        headerName: 'Effective Until',
        field: 'effectiveUntil',
        sortable: true,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString() : ''),
      },
    ];

    const monthColumns = (months || []).map((m) => ({
      headerName: monthLabel(m),
      field: m,
      sortable: true,
      valueFormatter: (p) => {
        if (p.value === null || p.value === undefined) return '-';
        if (typeof p.value === 'string' && p.value.trim() === '') return '';
        return moment(p.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
      },
      cellStyle: (params) => getReportCellStyle(params.value, m),
      comparator: (_a, _b, nodeA, nodeB) => {
        // sort: reported first, then not-reported (active), then inactive
        const rank = (v) => (v ? 2 : v === '' ? 1 : 0);
        return rank(nodeB.data?.[m]) - rank(nodeA.data?.[m]);
      },
    }));

    return [...base, ...monthColumns];
  }, [months, getReportCellStyle]);

  const gridKey = data.length ? 'monthly-reporting-loaded' : 'monthly-reporting-empty';

  const pickerValue = editingPicker === 'start' ? startMonth : editingPicker === 'end' ? endMonth : startMonth;

  return (
    <Box width="100%" padding="$4">
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="$4">
        <Heading level={2} sx={{ textAlign: 'left' }}>
          Monthly Reporting Status
        </Heading>

        <Box display="flex" flexDirection="row" gap="$3" alignItems="center">
          <TextField
            value={monthLabel(startMonth)}
            readOnly
            onPress={() => {
              setEditingPicker('start');
              setDatePickerOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              padding: '6px 10px',
              minWidth: 140,
            }}
          />
          <TextField
            value={monthLabel(endMonth)}
            readOnly
            onPress={() => {
              setEditingPicker('end');
              setDatePickerOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              padding: '6px 10px',
              minWidth: 140,
            }}
          />
        </Box>
      </Box>

      <AppLoadingOverlay loading={loading} message="Loading monthly reporting..." />

      {datePickerOpen && (
        <Dialog
          open={datePickerOpen}
          onClose={() => {
            setDatePickerOpen(false);
            setEditingPicker(null);
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" padding="$8">
            <DatePicker
              // Month-only picker: select a month (no day grid).
              date={moment(pickerValue, 'YYYY-MM-DD')}
              initialView={CalendarViewTypes.month}
              views={[CalendarViewTypes.month]}
              disableHeader={true}
              disableHighlightToday={true}
              CalendarProps={{
                monthSelectNextView: null, // don't switch into day view after selecting a month
                onMonthSelect: (yyyymm) => {
                  const next = moment(yyyymm, 'YYYYMM').startOf('month').format('YYYY-MM-DD');
                  if (editingPicker === 'start') {
                    setStartMonth(next);
                    if (moment(next).isAfter(moment(endMonth))) {
                      setEndMonth(next);
                    }
                  } else if (editingPicker === 'end') {
                    setEndMonth(next);
                    if (moment(next).isBefore(moment(startMonth))) {
                      setStartMonth(next);
                    }
                  }
                  setDatePickerOpen(false);
                  setEditingPicker(null);
                },
              }}
            />
          </Box>
        </Dialog>
      )}

      <Box display="flex" flexDirection="row" alignItems="center" gap="$4" marginBottom="$4">
        <span style={{ fontWeight: 'bold' }}>Legend:</span>

        <Box display="flex" flexDirection="row" alignItems="center" gap="$1">
          <Box width="16px" height="16px" backgroundColor="#DCFCE7" style={{ borderRadius: '6px', border: '1px solid #22C55E' }} />
          <span>Inventory Reported</span>
        </Box>

        <Box display="flex" flexDirection="row" alignItems="center" gap="$1">
          <Box width="16px" height="16px" backgroundColor="#FEE2E2" style={{ borderRadius: '6px', border: '1px solid #EF4444' }} />
          <span>No Inventory Reported</span>
        </Box>

        <Box display="flex" flexDirection="row" alignItems="center" gap="$1">
          <Box width="16px" height="16px" backgroundColor="#F3F4F6" style={{ borderRadius: '6px', border: '1px solid #E5E7EB' }} />
          <span>Not Active</span>
        </Box>
      </Box>

      <Grid
        key={gridKey}
        data={data}
        columns={columns}
        loading={loading}
        pageSize={20}
        shouldShowPagination={true}
        floatingFilter={false}
        setGridReference={() => {}}
      />
    </Box>
  );
};
